import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Loader2, MailCheck, SearchX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { APP_CONFIG } from '@/config/app';
import { useAuth } from '@/hooks/useAuth';
import { isCustomerUser } from '@/integrations/supabase/session';
import { usePortalBranding } from '../hooks/usePortalCompany';
import { PortalRateLimited, useRequestPortalLink } from '../hooks/usePortalActions';
import { safeReturnTo } from '../returnTo';
import { PortalBrandHeader } from '../components/PortalBrandHeader';
import { PortalLoading } from '../components/PortalLoading';
import { PortalErrorState } from '../components/PortalErrorState';
import PortalStaffBlocked from './PortalStaffBlocked';

/** Matches the Supabase Email OTP expiry (dashboard default 3600 s). */
const LINK_EXPIRES_MINUTES = 60;

export default function PortalLogin() {
  const { slug = '' } = useParams<{ slug: string }>();
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const branding = usePortalBranding(slug);
  const request = useRequestPortalLink();

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const from = safeReturnTo((location.state as { from?: string } | null)?.from);

  if (authLoading) return <PortalLoading fullScreen label="Checking your sign-in…" />;
  if (user && isCustomerUser(user)) return <Navigate to={from ?? `/portal/${slug}/home`} replace />;
  if (user) return <PortalStaffBlocked />;

  if (branding.isLoading) return <PortalLoading fullScreen />;
  if (branding.error) return <PortalErrorState fullScreen onRetry={() => void branding.refetch()} />;

  const brand = branding.data;
  if (!brand) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <SearchX className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <h1 className="mb-2 text-lg font-semibold">Page not found</h1>
            <p className="text-sm text-muted-foreground">
              We couldn't find a customer portal at this address. Please use the link from your email.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const name = brand.trading_name || brand.name;
  const primaryColor = brand.primary_color && /^#[0-9a-f]{6}$/i.test(brand.primary_color)
    ? brand.primary_color
    : 'hsl(var(--primary))';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setNotice(null);
    try {
      await request.mutateAsync({ slug, email: value });
      setSent(true);
    } catch (err) {
      if (err instanceof PortalRateLimited) {
        setNotice('Too many sign-in requests for now. Please wait a little while and try again.');
      } else {
        setNotice('We could not send the link just now. Please try again in a moment.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-muted flex flex-col">
      <div className="h-1.5 w-full" style={{ backgroundColor: primaryColor }} />
      <div className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="items-center text-center">
            <PortalBrandHeader name={name} logoUrl={brand.logo_url} primaryColor={primaryColor} className="mb-2" />
            <CardTitle className="text-xl">Your account</CardTitle>
            <CardDescription>
              View your quotes and invoices from {name}, accept a quote or pay online.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="text-center">
                <MailCheck className="mx-auto mb-3 h-10 w-10 text-success" />
                <p className="font-medium">Check your email</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  If that address is on file, a sign-in link is on its way. It expires in {LINK_EXPIRES_MINUTES} minutes.
                </p>
                <Button
                  variant="link"
                  className="mt-4"
                  onClick={() => { setSent(false); setNotice(null); }}
                >
                  Use a different email
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="portal-email">Email address</Label>
                  <Input
                    id="portal-email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Use the email address {name} has for you. We'll send a one-time sign-in link — no password needed.
                  </p>
                </div>
                {notice && (
                  <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{notice}</div>
                )}
                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  style={{ backgroundColor: primaryColor }}
                  disabled={request.isPending || !email.trim()}
                >
                  {request.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                  ) : (
                    'Email me a sign-in link'
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
      <p className="pb-6 text-center text-xs text-muted-foreground">
        {brand.phone && <>Questions? Call {name} on {brand.phone}. </>}
        Powered by {APP_CONFIG.name}
      </p>
    </div>
  );
}
